import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { SharedService } from '@services/api/shared.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { ReduxService } from '@state-management/redux.service';
import { IListingApproval, IListingBed, IListingPropertyFilter } from '../../core/models/listing-property.model';

@Injectable({
  providedIn: 'root',
})
export class ListingService {
  constructor(
    private api: ApiService,
    private localStorageService: LocalStorageService,
    private redux: ReduxService,
    private _sharedService: SharedService
  ) {}

  listingPropertyOption: any = {
    originalAssigns: [],
    property_Types: [],
    property_Status: [],
    appraisal_Status: [],
    lead_source: [],
    listing_Type: [],
    contractStatus: [],
    contactType: [],
    relatedContactTags: [],
    commission_payable_array: [],
    land_building_area_type: [],
    property_Status_rental: [],
    property_Status_no_rental: [],
    property_features: [],
    allSuburbs: [],
  };

  public user_role = Crypto.decrypt(this.localStorageService.GetItem('role'))?.toLowerCase();

  filter_param: IListingPropertyFilter = {
    office_id: this.api.getOfficeId(),
    keyword: '',
    property_type: null,
    suburb: null,
    listing_status: null,
    listing_type: null,
    applyFilterByField: null,
    agent: null,
    contract_status: null,
    listing_approval: null,
    date_range: null,
    beds: null,
    features: null,
    landMin: null,
    landMax: null,
    buildingMin: null,
    buildingMax: null,
  };

  approval_filter: IListingApproval[] = [
    { display: 'Approval Required', value: 'approval_required' },
    { display: 'Pending Approval', value: 'approval_pending' },
    { display: 'Changes Required', value: 'approval_comments' },
    { display: 'Approved', value: 'approved' },
  ];

  originalBeds: IListingBed[] = [
    { bed: '0' },
    { bed: '1' },
    { bed: '2' },
    { bed: '3' },
    { bed: '4' },
    { bed: '5' },
    { bed: '6' },
    { bed: '7' },
    { bed: '8' },
    { bed: '9' },
    { bed: '10' },
  ];

  getAgentList() {
    this._sharedService.getModuleAgent('listing').subscribe({
      next: response => {
        let activeAgent = response?.data?.filter(agent => agent?.deactive === false) || [];
        this.listingPropertyOption.originalAssigns = activeAgent;
      },
    });
  }

  roles: any[] = [];
  Teams: any[] = [];

  getRoles() {
    /**
     * Fetches all roles for the current office, excluding the 'Master Admin' role.
     */
    this.api.get(`/roles?per_page=all&office_id=${this.api.getOfficeId()}`).subscribe(data => {
      // Filter out roles with the name 'Master Admin'
      this.roles = data.data?.filter(el => el?.name?.toLowerCase() !== 'master admin');
    });
  }

  getTeams() {
    /**
     * Fetches and assigns teams for the current office.
     * @returns {void}
     */
    this.api.get('/teams?office_id=' + this.api.getOfficeId()).subscribe(data => {
      // Assign the fetched team data to the Teams property
      this.Teams = data.data;
    });
  }

  getPropertyFeatures() {
    /**
     * Fetches and processes all property features for the current office.
     *
     * @returns {Promise<any[]>} A promise that resolves with an array of property features.
     */
    this._sharedService.getPropertyListingFeature().subscribe({
      next: (response: any) => {
        this.listingPropertyOption.property_features = [];
        // Sort the property features alphabetically by feature name
        const sortedData = [...(response?.data || [])].sort((a, b) => a?.feature_name.localeCompare(b?.feature_name));
        this.listingPropertyOption.property_features = sortedData?.map(ele => {
          ele = { ...ele, feature_name: ele.feature_name === 'FloorBoards' ? 'Floorboards' : ele.feature_name };
          return ele;
        });
      },
    });
  }

  getTags() {
    /**
     * Fetches and processes listing-related tags for the current office.
     */
    this.redux.getTags(this.api.getOfficeId(), true, 'listing').subscribe(res => {
      if (res?.data?.length > 0) {
        res?.data[0]?.types?.forEach(ele => {
          // Sort the tag data alphabetically by tag name
          const sortedData = [...(ele?.data || [])].sort((a, b) => a?.tag_name.localeCompare(b?.tag_name));

          switch (ele?.slug) {
            case 'property-type':
              // Assign sorted property types
              this.listingPropertyOption.property_Types = sortedData;
              break;
            case 'listing-status':
              // Assign sorted listing statuses
              this.listingPropertyOption.property_Status = sortedData;
              // Set filter parameter for property manager role
              if (this.user_role === 'property manager') {
                const status = this.listingPropertyOption.property_Status.find(el => el?.tag_name?.toLowerCase() === 'for lease');
                this.filter_param.listing_status = status ? [status.tag_name] : [];
              }
              break;
            case 'listing-type':
              // Assign sorted listing types
              this.listingPropertyOption.listing_Type = sortedData;
              break;
            case 'appraisal-status':
              // Assign sorted appraisal statuses and categorize property statuses
              this.listingPropertyOption.appraisal_Status = sortedData;
              this.listingPropertyOption.property_Status_rental = this.listingPropertyOption.property_Status?.filter(
                el =>
                  el?.tag_name?.toLowerCase() === 'deposite taken' ||
                  el?.tag_name?.toLowerCase() === 'for lease' ||
                  el?.tag_name?.toLowerCase() === 'leased' ||
                  el?.tag_name?.toLowerCase() === 'off market'
              );
              this.listingPropertyOption.property_Status_no_rental = this.listingPropertyOption.property_Status?.filter(
                el =>
                  el?.tag_name?.toLowerCase() === 'draft' ||
                  el?.tag_name?.toLowerCase() === 'off market' ||
                  el?.tag_name?.toLowerCase() === 'for sale' ||
                  el?.tag_name?.toLowerCase() === 'settled' ||
                  el?.tag_name?.toLowerCase() === 'settlement' ||
                  el?.tag_name?.toLowerCase() === 'sold' ||
                  el?.tag_name?.toLowerCase() === 'under offer'
              );
              break;
            case 'lead-source':
              // Assign sorted lead sources
              this.listingPropertyOption.lead_source = sortedData;
              break;
            case 'contact-type':
              // Assign sorted contact types
              this.listingPropertyOption.contactType = sortedData;
              break;
            case 'related-contact':
              // Assign sorted related contact tags
              this.listingPropertyOption.relatedContactTags = sortedData;
              break;
            case 'commission-payable':
              // Assign sorted commission payable data
              this.listingPropertyOption.commission_payable_array = sortedData;
              break;
            case 'land-building-area-type':
              // Assign sorted commission payable data
              this.listingPropertyOption.land_building_area_type = sortedData;
              break;
          }
        });
      }
    });
  }

  getContractStatus() {
    /**
     * Fetches the contract tags for the current office.
     * @param {string} office_id - The id of the office.
     * @returns {Promise<any[]>} - A promise that resolves with an array of tag objects.
     */
    this.redux.getTags(this.api.getOfficeId(), false, 'contract').subscribe({
      next: res => {
        if (res?.data?.length > 0) {
          // Iterate over the contract tags and sort them alphabetically
          res?.data[0]?.types?.forEach(ele => {
            const sortedData = [...(ele?.data || [])].sort((a, b) => a?.tag_name.localeCompare(b?.tag_name));
            // Extract the contract status tags
            switch (ele?.slug) {
              case 'contract-status':
                this.listingPropertyOption.contractStatus = sortedData;
                break;
            }
          });
        }
      },
    });
  }

  getSuburbs() {
    /**
     * Get all the suburbs for the current office.
     * @param {string} office_id - The id of the office.
     * @returns {Promise<any[]>} - A promise that resolves with an array of suburb objects.
     */
    this._sharedService.getSuburbList().subscribe({
      next: response => {
        /**
         * The array of suburb objects
         * @type {any[]}
         */
        this.listingPropertyOption.allSuburbs = response?.data?.map(suburb => ({ name: suburb })) || [];
      },
      error: error => {
        console.error('Error fetching suburbs:', error);
        this.listingPropertyOption.allSuburbs = [];
      },
    });
  }
}
